import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Input,
  Space,
  Tag,
  Typography,
  message,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalService, type ApprovalWorkflowDefinitionDTO } from '@/services/approval.service';
import { approvalWorkflowEntityTypeDisplay } from '@/constants/approval-entity-types';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

export const ApprovalWorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cloneTarget, setCloneTarget] = useState<{ id: string } | null>(null);
  const [cloneCode, setCloneCode] = useState('');
  const [searchText, setSearchText] = useState('');

  const { data: workflows = [], isLoading } = useQuery({
    queryKey: ['approval-definitions'],
    queryFn: () => approvalService.listDefinitions(),
  });

  const filteredWorkflows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return workflows;
    return workflows.filter((w) => (w.workflow_name || '').toLowerCase().includes(q));
  }, [workflows, searchText]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => approvalService.deleteDefinition(id),
    onSuccess: () => {
      message.success('Workflow archived');
      queryClient.invalidateQueries({ queryKey: ['approval-definitions'] });
    },
  });

  const cloneMutation = useMutation({
    mutationFn: ({ id, code }: { id: string; code: string }) => approvalService.cloneDefinition(id, code),
    onSuccess: () => {
      message.success('Cloned');
      queryClient.invalidateQueries({ queryKey: ['approval-definitions'] });
    },
  });

  const columns: ColumnsType<ApprovalWorkflowDefinitionDTO> = [
    {
      title: 'Name',
      dataIndex: 'workflow_name',
      key: 'workflow_name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Code',
      dataIndex: 'workflow_code',
      key: 'workflow_code',
    },
    {
      title: 'Entity type',
      dataIndex: 'entity_type',
      key: 'entity_type',
      render: (t) => <Tag color="blue">{approvalWorkflowEntityTypeDisplay(t)}</Tag>,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 80,
    },
    {
      title: 'Amount band',
      key: 'amt',
      render: (_, r) =>
        r.amount_min != null || r.amount_max != null ? (
          <Tag>
            {r.amount_min ?? '—'} – {r.amount_max ?? '—'}
          </Tag>
        ) : (
          <Tag color="green">Any</Tag>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (a) => <Tag color={a ? 'success' : 'default'}>{a ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/platform/workflows/approvals/${row.id}/edit`)}>
            Edit
          </Button>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => {
              setCloneTarget({ id: row.id });
              setCloneCode(`${row.workflow_code}_COPY`);
            }}
          >
            Clone
          </Button>
          <Popconfirm title="Archive this workflow?" onConfirm={() => deleteMutation.mutate(row.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              Archive
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={2}>Approval Workflows</Title>
      <Paragraph type="secondary">
        Configure universal approval definitions. Purchase Orders use entity type <Text code>purchase_order</Text> when{' '}
        <Text code>USE_UNIVERSAL_APPROVAL_FOR_PO</Text> is enabled on the API. EPC Subcontractor Admin entity types
        (Agreement, SPC, Variation, Claim, EOT, Final Account, Closeout) appear in the entity-type dropdown — create
        one active workflow per type for the current company (or run{' '}
        <Text code>setup-subcontract-admin-approval-workflows.ts</Text>).
      </Paragraph>

      <Card
        title="Definitions"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/platform/workflows/approvals/new')}>
            New workflow
          </Button>
        }
      >
        <div style={{ marginBottom: 16, maxWidth: 360 }}>
          <Search
            placeholder="Search by name"
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={filteredWorkflows}
          pagination={{
            defaultPageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${total} workflow${total === 1 ? '' : 's'}`,
          }}
        />
      </Card>

      <Modal
        title="Clone workflow"
        open={!!cloneTarget}
        onCancel={() => setCloneTarget(null)}
        onOk={() => {
          if (!cloneTarget || !cloneCode.trim()) return;
          cloneMutation.mutate({ id: cloneTarget.id, code: cloneCode.trim() });
          setCloneTarget(null);
        }}
        confirmLoading={cloneMutation.isPending}
      >
        <Input value={cloneCode} onChange={(e) => setCloneCode(e.target.value)} placeholder="New workflow code" />
      </Modal>
    </div>
  );
};
